import MemberButton from '@/member/components/MemberButton'
import { Box, HStack, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { Draggable, Droppable } from '@hello-pangea/dnd'
import { ParticipantMember } from '@rolebase/shared/model/member'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MEETING_MEMBERS_DND_TYPE,
  MEETING_MEMBERS_DROPPABLE_PREFIX,
} from './MeetingThreadsDragDropContext'

interface Props {
  stepId: string
  // Already ordered: inherited first, newcomers at the end (REU89-D11).
  members: ParticipantMember[]
  readOnly?: boolean
  onReport: (stepId: string, membersIds: string[]) => void
}

// The round's order, in the "by member" view only. Any participant may change
// it, like the weather: the facilitator's hand is a usage, not a lock.
export default function MeetingThreadsMembersBar({
  stepId,
  members,
  readOnly,
  onReport,
}: Props) {
  const { t } = useTranslation()

  // Tell the drag context what the eye currently sees, so the very first drag
  // has an order to move things in, before anything was ever written.
  useEffect(() => {
    onReport(
      stepId,
      members.map((p) => p.member.id)
    )
  }, [stepId, members, onReport])

  return (
    <Box mb={4}>
      <Text fontSize="sm" color="gray.500" mb={1}>
        {t('MeetingThreadsMembersBar.title')}
      </Text>
      <Droppable
        droppableId={`${MEETING_MEMBERS_DROPPABLE_PREFIX}${stepId}`}
        type={MEETING_MEMBERS_DND_TYPE}
        direction="horizontal"
        isDropDisabled={readOnly}
      >
        {(provided) => (
          <Wrap
            ref={provided.innerRef}
            {...provided.droppableProps}
            spacing={1}
          >
            {members.map((participant, index) => (
              <Draggable
                key={participant.member.id}
                draggableId={`${stepId}-${participant.member.id}`}
                index={index}
                isDragDisabled={readOnly}
              >
                {(dragProvided) => (
                  <WrapItem
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    cursor={readOnly ? undefined : 'grab'}
                  >
                    <HStack spacing={1}>
                      <Text fontSize="xs" color="gray.500">
                        {index + 1}
                      </Text>
                      {/* The name opens nothing here, it is only held to move
                          the member. It must not catch the pointer: the drag
                          library refuses to start a drag on a <button>
                          (`interactiveTagNames`), so only the rank could be
                          grabbed (#106). */}
                      <MemberButton
                        member={participant.member}
                        size="sm"
                        pointerEvents="none"
                        tabIndex={-1}
                      />
                    </HStack>
                  </WrapItem>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Wrap>
        )}
      </Droppable>
    </Box>
  )
}
